import { ErrorMessage } from '@hookform/error-message'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { useGetText } from 'shared/i18n'
import { Credentials } from 'shared/auth'
import { SubmitButton } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { NavLink } from 'react-router-dom'

type LoginFormProps = {
  onSubmit: (credentials: Credentials) => Promise<void>
  className?: string
}

export default function LoginForm(props: LoginFormProps) {
  const { onSubmit, className } = props
  const { gettext } = useGetText()

  const schema = yup.object({
    username: yup.string().required(gettext('No username or email provided')),
    password: yup.string().required(gettext('No password provided')),
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { username: '', password: '' },
    resolver: yupResolver(schema),
  })

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <h1 className="mb-6 font-serif text-5xl font-semibold">
        {gettext('Log In')}
      </h1>

      <fieldset className="flex flex-col gap-y-4" disabled={isSubmitting}>
        <div>
          <label className="mb-1 inline-block text-gray-drh" htmlFor="username">
            {gettext('Username / Email')}
          </label>

          <Input
            className="w-full"
            isInvalid={!!errors?.username}
            id="username"
            autoCorrect="off"
            autoComplete="username"
            {...register('username')}
          />

          <ErrorMessage
            errors={errors}
            name="username"
            as={<p className="mt-1 text-xs text-red-600" />}
          />
        </div>

        <div>
          <label className="mb-1 inline-block text-gray-drh" htmlFor="password">
            {gettext('Password')}
          </label>

          <Input
            autoComplete="current-password"
            className="w-full"
            isInvalid={!!errors?.password}
            id="password"
            type="password"
            {...register('password')}
          />

          <ErrorMessage
            errors={errors}
            name="password"
            as={<p className="mt-1 text-xs text-red-600" />}
          />
        </div>

        <p className="mb-2">
          <NavLink to="/forgot-password" className="link text-sm">
            {gettext('Forgot your password?')}
          </NavLink>
        </p>

        <SubmitButton
          className="mr-auto w-full sm:w-auto"
          disabled={isSubmitting}
        >
          {isSubmitting ? gettext('Logging In…') : gettext('Log In')}
        </SubmitButton>
      </fieldset>
    </form>
  )
}
